import { chunkRecoveryStatus } from '@core/utils/error-handling/chunk-recovery-status'
import {
  classifyError,
  type ErrorKind,
} from '@core/utils/error-handling/classify-error'
import { retryAfterError } from '@core/utils/error-handling/retry-after-error'
import type { Stylable } from '@core/utils/types'
import { Button } from '@design/components/buttons/button/Button'
import { IconCircle } from '@design/components/icons/icon-circle/IconCircle'
import { errorTexts } from '@design/utils/error-texts'
import { focusHeadingAndSetTitle } from '@design/utils/focus-heading-and-set-title'
import { Link, useRouter } from '@tanstack/react-router'
import { cn } from 'cn'
import { RefreshCw, TriangleAlert, WifiOff } from 'lucide-react'
import type { ElementType } from 'react'
import { useTranslation } from 'react-i18next'

export type RouteErrorScreenProps = Stylable & {
  error: unknown
  onRetry?: () => void
  isRetrying?: boolean
}

const errorIcons: Record<ErrorKind, ElementType> = {
  chunk: RefreshCw,
  network: WifiOff,
  unexpected: TriangleAlert,
}

export function RouteErrorScreen({
  error,
  onRetry,
  isRetrying,
  className,
  style,
}: RouteErrorScreenProps) {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const kind = classifyError(error)
  const texts = errorTexts(t)[kind]

  if (chunkRecoveryStatus.isReloading) return null

  return (
    <div
      lang={i18n.language}
      className={cn(
        'flex min-h-[60svh] flex-1 flex-col items-center justify-center gap-2 p-8 text-center',
        className
      )}
      style={style}
    >
      <IconCircle icon={errorIcons[kind]} className='mb-2 size-14 p-4' />
      <h1
        ref={focusHeadingAndSetTitle}
        tabIndex={-1}
        className='font-semibold text-2xl tracking-tight focus:outline-hidden'
      >
        {texts.title}
      </h1>
      <p className='max-w-md text-muted-foreground'>{texts.description}</p>
      <div className='mt-5 flex flex-wrap justify-center gap-3'>
        <Button
          isLoading={isRetrying}
          onClick={() =>
            retryAfterError(kind, onRetry ?? (() => router.invalidate()))
          }
        >
          {texts.action}
        </Button>
        <Button asChild variant='outline'>
          <Link to='/' reloadDocument={kind === 'chunk'}>
            {t('designSystem.components.ui.backToHome')}
          </Link>
        </Button>
      </div>
    </div>
  )
}
